/**
 * Parse a Keep-a-Changelog / standard-version / git-cliff style markdown file
 * into WhatsNewSection entries.
 *
 * Recognised structures (a project may mix several):
 *
 *     ## [Version] - YYYY-MM-DD            (Keep-a-Changelog)
 *     ## Version (YYYY-MM-DD)              (git-cliff)
 *     ### [Version](compare-url) (YYYY-MM-DD)   (standard-version)
 *     ### Category                         (Added / Fixed / 🚀 Features / ...)
 *     - bullet text                        (also `*` or `+`)
 *       continuation indented under it
 *
 * Each leaf bullet becomes one entry. The `date` field falls back to the
 * version string when the version header has no date (e.g. `## [Unreleased]`).
 *
 * `###` is overloaded by some generators (it can mean either a sub-version
 * header like `### [1.1.3](...) (2026-02-20)` or a category like
 * `### 🐛 Bug Fixes`). We disambiguate with a structural heuristic: if the
 * line contains a `[v?X.Y...]` token or a parenthesised ISO date, it's a
 * version header; otherwise it's a category.
 */

function categoryToTag(category) {
    if (!category) return undefined;
    const c = category.toLowerCase();
    if (/(add|feat|new)/.test(c)) return 'feature';
    if (/(fix|bug|security|removed?|deprecat)/.test(c)) return 'fix';
    if (/(release)/.test(c)) return 'release';
    return 'change';
}

function cleanInline(text) {
    return text
        // markdown links → link text only (drops commit-hash links etc.)
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // emphasis & code
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '$1')
        // trailing bare commit hash in parens, e.g. "... fix (48c6de9)"
        .replace(/\s*\(\b[0-9a-f]{6,40}\b\)\s*\.?\s*$/i, '')
        // collapse the rare double-space the link strip can leave
        .replace(/\s{2,}/g, ' ');
}

function makeEntry(version, date, category, bullet) {
    const text = cleanInline(bullet).trim();

    let label;
    let description;
    const colonIdx = text.indexOf(':');
    if (colonIdx > 0 && colonIdx < 80) {
        label = text.slice(0, colonIdx).trim();
        description = text.slice(colonIdx + 1).trim();
    } else {
        const sentenceMatch = text.match(/^(.{10,80}?[.!?])(\s+|$)/);
        if (sentenceMatch) {
            label = sentenceMatch[1].replace(/[.!?]+$/, '').trim();
            description = text.slice(sentenceMatch[0].length).trim();
        } else {
            label = text;
            description = '';
        }
    }

    return {
        date: date || version || '',
        label,
        description,
        tag: categoryToTag(category),
    };
}

function isVersionHeader(line) {
    if (line.startsWith('## ') && !line.startsWith('### ')) return true;
    if (!line.startsWith('### ')) return false;
    // ### is ambiguous — only treat as version when it carries version-like signal
    return /\[v?\d+(?:\.\d+)+[^\]]*\]/.test(line) || /\(\d{4}-\d{2}-\d{2}\)/.test(line);
}

function parseVersionHeader(line) {
    const stripped = line.replace(/^#+\s+/, '').trim();

    // [Version](compare-url) (YYYY-MM-DD)         standard-version
    let m = stripped.match(/^\[([^\]]+)\]\([^)]+\)(?:\s*\(([^)]+)\))?\s*$/);
    if (m) return { version: m[1].trim(), date: (m[2] || '').trim() };

    // [Version] - date          Keep-a-Changelog
    m = stripped.match(/^\[([^\]]+)\](?:\s*[-–—]\s*(.+?))?\s*$/);
    if (m) return { version: m[1].trim(), date: (m[2] || '').trim() };

    // Version (date)            git-cliff
    m = stripped.match(/^([^\s(]+)\s*\((.+?)\)\s*$/);
    if (m) return { version: m[1].trim(), date: m[2].trim() };

    // Version - date
    m = stripped.match(/^(\S+)\s*[-–—]\s*(.+?)\s*$/);
    if (m) return { version: m[1].trim(), date: m[2].trim() };

    return { version: stripped, date: '' };
}

export function parseChangelog(markdown) {
    if (!markdown || typeof markdown !== 'string') return [];

    const lines = markdown.split('\n');
    const entries = [];
    let version = null;
    let date = null;
    let category = null;
    let bullet = null;

    const flush = () => {
        if (bullet) {
            entries.push(makeEntry(version, date, category, bullet));
            bullet = null;
        }
    };

    for (const raw of lines) {
        const line = raw.replace(/\r$/, '');

        if (/^#{2,3}\s+/.test(line) && isVersionHeader(line)) {
            flush();
            const parsed = parseVersionHeader(line);
            version = parsed.version;
            date = parsed.date;
            category = null;
            continue;
        }

        const categoryMatch = line.match(/^###\s+(.+?)\s*$/);
        if (categoryMatch) {
            flush();
            // Strip leading emoji + whitespace (e.g. "🚀 Features" → "Features")
            category = categoryMatch[1].replace(/^[^A-Za-z0-9]+/, '').trim();
            continue;
        }

        const bulletMatch = line.match(/^[-*+]\s+(.+)$/);
        if (bulletMatch) {
            flush();
            bullet = bulletMatch[1].trim();
            continue;
        }

        if (bullet && /^\s+\S/.test(line)) {
            bullet += ' ' + line.trim();
            continue;
        }

        if (line.trim() === '') {
            flush();
        }
    }
    flush();

    return entries;
}
